<template>
  <div data-testid="lgpd-termos-table">
    <div class="table-toolbar">
      <Button
        :label="t('Cadastrar termo')"
        icon="pi pi-plus"
        severity="primary"
        size="small"
        data-testid="lgpd-termos-create-btn"
        @click="openCreateModal"
      />
      <Button
        :label="t('Atualizar')"
        icon="pi pi-refresh"
        severity="secondary"
        size="small"
        data-testid="lgpd-termos-refresh-btn"
        @click="loadData"
      />
    </div>

    <data-table
      ref="dataTableRef"
      :config="datatableConfig"
      :data="terms"
      :loading="isLoading"
      :refreshButton="{ show: false }"
      @edit-click="handleRowClick"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useLgpdAdmin } from '../../composables/useLgpdAdmin';
import type { LgpdTerms_ListRow } from '../../contracts/LgpdTerms_ListRow';

interface ButtonClickPayload {
  value: number;
  data?: LgpdTerms_ListRow;
  action: string;
}

const emit = defineEmits<{
  'view-acceptances': [termId: number];
}>();

const { t } = useI18n();
const { isLoading, listTerms } = useLgpdAdmin();

const terms = ref<LgpdTerms_ListRow[]>([]);
const dataTableRef = ref<{ loadData: () => void } | null>(null);

const datatableConfig = computed(() => ({
  tableId: 'lgpdTermosDataTable',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      width: 80,
      renderType: 'clickable_id',
    },
    {
      data: 'versao',
      title: t('Versão'),
      width: 100,
      filterType: 'text',
    },
    {
      data: 'titulo',
      title: t('Título'),
      filterType: 'text',
    },
    {
      data: 'ativo',
      title: t('Ativo'),
      width: 100,
      cellRenderer: ({ value }: { value: number }) => {
        if (value === 1) {
          return `<span class="p-tag p-tag-success">${t('Sim')}</span>`;
        }
        return `<span class="p-tag p-tag-secondary">${t('Não')}</span>`;
      },
    },
    {
      data: 'created_at',
      title: t('Criado em'),
      width: 180,
      renderType: 'datetime',
    },
    {
      data: 'created_by',
      title: t('Criado por'),
      width: 150,
      filterType: 'text',
    },
    {
      data: 'id',
      title: t('Ações'),
      width: 120,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Ativar'),
      buttonIcon: 'pi pi-check-circle',
      buttonClass: 'p-button-success p-button-sm',
      buttonAction: 'activate',
      buttonCondition: (rowData: LgpdTerms_ListRow) => rowData.ativo !== 1,
    },
  ],
  pagination: true,
  pageLength: 25,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.id;
    return id == null ? '' : `lgpd-termos-row-${id}`;
  },
}));

const loadData = async (): Promise<void> => {
  log('LgpdTermosTable', 'loadData', 'Loading terms');
  try {
    terms.value = await listTerms();
  } catch (err) {
    log('LgpdTermosTable', 'loadData', 'Error', err);
  }
};

const openCreateModal = (): void => {
  log('LgpdTermosTable', 'openCreateModal', 'Opening create modal');
  eventBus.emit('modal.lgpd-termo.open');
};

const handleRowClick = (id: number): void => {
  log('LgpdTermosTable', 'handleRowClick', 'Opening edit modal', id);
  eventBus.emit('modal.lgpd-termo.open', { id });
};

const handleButtonClick = (payload?: ButtonClickPayload): void => {
  if (!payload || payload.action !== 'activate') return;
  log('LgpdTermosTable', 'handleButtonClick', 'Activating term', payload.value);
  eventBus.emit('modal.lgpd-termo.open', { id: payload.value, focusActivate: true });
};

const handleTermCreated = (): void => { log('LgpdTermosTable', 'event:lgpd_term.created', 'Received'); loadData(); };
const handleTermUpdated = (): void => { log('LgpdTermosTable', 'event:lgpd_term.updated', 'Received'); loadData(); };
const handleTermDeleted = (): void => { log('LgpdTermosTable', 'event:lgpd_term.deleted', 'Received'); loadData(); };

onMounted(() => {
  loadData();
  eventBus.on('lgpd_term.created', handleTermCreated);
  eventBus.on('lgpd_term.updated', handleTermUpdated);
  eventBus.on('lgpd_term.deleted', handleTermDeleted);
});

onBeforeUnmount(() => {
  eventBus.off('lgpd_term.created', handleTermCreated);
  eventBus.off('lgpd_term.updated', handleTermUpdated);
  eventBus.off('lgpd_term.deleted', handleTermDeleted);
});

defineExpose({ loadData });
</script>

<style scoped>
.table-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
</style>
